import './FamilyPages.css';
import PhotoSlot from '../components/PhotoSlots';
import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Calendar, ExternalLink, Mail, MapPin, MessageCircle, Phone, Waves } from 'lucide-react';
import SEO from '../components/SEO';
import SwimPricing from '../components/SwimPricing';
import PoolHoursTable from '../components/PoolHoursTable';
import ProgramJourney from '../components/ProgramJourney';
import { ServiceBreadcrumb, ServiceFacts } from '../components/ServiceHeader';
import { swimLevels } from '../data/swimLevels';


/**
 * Swimming program landing page (Brentwood location only).
 * Hero → Canonical overview → Two-Pool callout → lesson options → Pricing → Pool Hours → CTA
 */




const SwimmingPage = () => {
  const swimSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Swim Lessons in Brentwood, CA',
    description:
      'Eight structured swim lesson options at Delta Valley Health Club in Brentwood for children ages 6 months and up and adults.',
    provider: {
      '@type': 'HealthClub',
      name: 'Delta Valley Health Club',
      telephone: '(925) 240-2990',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '120 Guthrie Ln',
        addressLocality: 'Brentwood',
        addressRegion: 'CA',
        postalCode: '94513',
      },
    },
    areaServed: ['Brentwood'],
    serviceType: 'Swim Lessons',
    audience: { '@type': 'Audience', audienceType: 'Children, adults, seniors' },
  };


  return (
    <div className="dv-family dv-swimming" data-testid="swimming-page">
      <SEO
        title="Swimming Lessons in Brentwood, CA | Delta Valley Health Club"
        description="Eight structured swimming lesson options for children and private lessons for adults in Brentwood."
        keywords="swim lessons Brentwood, swimming lessons near me, baby swim lessons, kids swim lessons, adult swim lessons, indoor pool Brentwood, swim instructor Brentwood"
        schema={swimSchema}
      />


      {/* HERO */}
      <section
        className="dv-family__hero"
      >
        <div className="dv-family__hero-copy">
          <div className="dv-service-panel">
            <ServiceBreadcrumb current="Swimming Lessons" />
            <h1 className="dv-size-heading dv-service-title mb-6" data-testid="swim-h1">
              Swim Lessons in Brentwood
            </h1>
            <p className="dv-size-lede text-blue-50/90 max-w-2xl font-light leading-relaxed">
              From your baby&apos;s first dip to advanced butterfly, choose from eight lesson options with certified instructors,
              and an indoor lesson pool kept at a toasty <strong>93°F</strong>.
            </p>
            <div className="dv-service-actions">
              <a className="dv-button dv-button--light" href="#swim-inquiry" data-testid="hero-register-btn">Request Swim Information</a>
              <a className="dv-button dv-button--outline-light" href="#pricing">See Pricing</a>
            </div>
          </div>
        </div>
        <PhotoSlot id="swim-hero" label="Swimming lessons — instructor and swimmer photo" className="dv-family__hero-media" />
        <ServiceFacts items={["Brentwood location only", "93°F indoor lesson pool", "Eight lesson options"]} />
      </section>


      <section className="bg-white py-16" data-testid="legacy-swim-overview">
        <div className="container-custom max-w-5xl">
          <h2 className="dv-size-heading text-4xl font-bold text-gray-900">Learn a life skill</h2>
          <p className="dv-size-body mt-5 text-lg leading-relaxed text-gray-700">
            Swimming is not only a fun fitness activity but also a lifesaving one. Early
            swimming lessons will help your children feel at home in the water and ensure
            they learn a fun way to exercise for life. We offer group, private, and
            semi-private lessons for children and private lessons for adults. There is
            always a lifeguard on duty during group lessons.
          </p>
          <p className="dv-size-body mt-4 text-lg leading-relaxed text-gray-700">
            Use the swim inquiry form below to reach the Aquatics Team. Each request is
            delivered to Liz and the Delta Valley Kids team for follow-up.
          </p>
          <div className="mt-6 flex flex-col gap-3 text-base sm:flex-row sm:flex-wrap sm:gap-6">
            <a className="inline-flex items-center gap-2 font-semibold text-[#0072BB] hover:underline" href="tel:+19252402990">
              <Phone size={18} /> Call (925) 240-2990
            </a>
            <a className="inline-flex items-center gap-2 font-semibold text-[#0072BB] hover:underline" href="sms:+19253083272">
              <MessageCircle size={18} /> Text (925) 308-3272
            </a>
            <a className="inline-flex items-center gap-2 font-semibold text-[#0072BB] hover:underline" href="mailto:lizh@deltavac.com">
              <Mail size={18} /> Email the Aquatics Team
            </a>
          </div>
          <a href="#swim-inquiry" className="mt-7 inline-block">
            <Button>Contact Us Now</Button>
          </a>
        </div>
      </section>


      {/* TWO POOLS */}
      <section className="py-16 bg-white" data-testid="two-pools-section">
        <div className="container-custom max-w-5xl">
          <div className="dv-family__section-heading">
            <div className="text-xs uppercase tracking-widest text-[#0072BB] font-bold mb-3">Brentwood Aquatics</div>
            <h2 className="dv-size-heading text-4xl md:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              Two indoor pools, year-round.
            </h2>
            <p className="dv-size-lede text-lg text-gray-600 max-w-2xl mx-auto">
              Lessons use our 93°F warm-water teaching pool, with some advanced lessons using the Lap Pool.
              Family swim and lap swim use our 25-yard lap pool.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="dv-family__panel">

              <CardContent className="p-6">
                <Waves size={28} className="text-[#0072BB] mb-3" />
                <h3 className="dv-size-subheading text-2xl font-bold text-gray-900 mb-1">Lap Pool</h3>
                <div className="dv-size-subheading text-3xl font-black text-[#0072BB] mb-3">25 yards · 83°F</div>
                <p className="dv-size-body text-gray-700 mb-4">
                  Four lanes are available to members during club operating hours, with scheduled Water Aerobics and Advanced Swim Conditioning sessions.
                </p>
                <Link to={publicHref("/amenities/pool")} className="text-sm text-[#0072BB] hover:underline font-semibold inline-flex items-center gap-1">
                  Learn more about our pool <ArrowRight size={14} />
                </Link>
              </CardContent>
            </Card>
            <Card className="dv-family__panel">

              <CardContent className="p-6">
                <Waves size={28} className="text-cyan-600 mb-3" />
                <h3 className="dv-size-subheading text-2xl font-bold text-gray-900 mb-1">Lesson &amp; Therapy Pool</h3>
                <div className="dv-size-subheading text-3xl font-black text-cyan-600 mb-3">15 yards · 93°F</div>
                <p className="dv-size-body text-gray-700 mb-4">
                  Used for swim lessons, senior physical therapy and scheduled beach parties. Warm enough for babies, gentle on joints.
                </p>
                <a href="#pool-hours" className="text-sm text-cyan-700 hover:underline font-semibold inline-flex items-center gap-1">
                  See pool hours <ArrowRight size={14} />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* CANONICAL LESSON OPTIONS */}
      <section className="dv-family__section dv-family__section--paper" data-testid="swim-levels-section">
        <div className="container-custom max-w-6xl">
          <div className="dv-family__section-heading">
            <div className="text-xs uppercase tracking-widest text-[#0072BB] font-bold mb-3">Curriculum</div>
            <h2 className="dv-size-heading text-4xl md:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              Swimming lessons for ages 6 months through adults.
            </h2>
            <p className="dv-size-lede text-lg text-gray-600 max-w-2xl mx-auto">
              Each lesson is a structured 25 minutes with an instructor who knows exactly what your swimmer
              needs to progress.
            </p>
          </div>


          <div className="dv-family__lesson-grid dv-family__lesson-grid--levels">
            {swimLevels.map((lvl) => (
              <Card key={lvl.slug} className="dv-family__lesson" data-testid={`level-card-${lvl.slug}`}>

                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="dv-size-subheading text-4xl">{lvl.icon}</div>
                      {lvl.classification && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-[#005A94] bg-blue-50 px-2 py-0.5 rounded-full" data-testid={`level-classification-${lvl.slug}`}>
                          {lvl.classification}
                        </span>
                      )}
                    </div>
                    {lvl.level && (
                      <Badge className="border-0 bg-blue-50 text-xs text-[#005A94] hover:bg-blue-50">
                        {lvl.level}
                      </Badge>
                    )}
                  </div>
                  <h3 className="dv-size-subheading text-xl font-bold text-gray-900 mb-1">{lvl.name}</h3>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-3">{lvl.ageRange}</div>
                  {lvl.ratio && (
                    <div className="mb-3 text-sm font-semibold text-[#005A94]">Student-to-instructor ratio: {lvl.ratio}</div>
                  )}
                  <p className="dv-size-body text-sm text-gray-700 leading-relaxed mb-3">{lvl.summary}</p>
                  {lvl.blocks?.length > 0 && (
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-bold text-[#0072BB] hover:text-[#005A94] uppercase tracking-wide" data-testid={`level-expand-${lvl.slug}`}>
                        Lesson activities ↓
                      </summary>
                      <div className="mt-3 space-y-2 text-xs text-gray-700">
                        {lvl.blocks.map((b, i) => (
                          <div key={i} className="border-l-2 border-gray-200 pl-3">
                            <div className="font-bold text-gray-900 mb-0.5">{b.mins} min</div>
                            <ul className="space-y-0.5">
                              {b.items.map((it, j) => (
                                <li key={j} className="dv-size-body text-gray-600 leading-snug">• {it}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {lvl.levelUpRequirements?.length > 0 && (
                    <details className="group mt-3">
                      <summary className="cursor-pointer text-xs font-bold text-[#0072BB] hover:text-[#005A94] uppercase tracking-wide" data-testid={`level-requirements-${lvl.slug}`}>
                        Requirements to level up ↓
                      </summary>
                      <ul className="mt-3 space-y-1 border-l-2 border-gray-200 pl-3 text-xs text-gray-600">
                        {lvl.levelUpRequirements.map((requirement) => (
                          <li key={requirement} className="dv-size-body leading-snug">• {requirement}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {lvl.registrationUrl && (
                    <a
                      href={publicHref(lvl.registrationUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center text-sm font-bold text-[#0072BB] hover:underline"
                    >
                      {lvl.availabilityLabel} <ExternalLink className="ml-1" size={14} />
                    </a>
                  )}
                  {!lvl.registrationUrl && (
                    <span className="mt-4 inline-flex text-sm font-bold text-gray-500">
                      {lvl.availabilityLabel}
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* PRICING */}
      <section id="pricing" className="py-20 bg-white scroll-mt-20" data-testid="pricing-section">
        <div className="container-custom max-w-5xl">
          <div className="dv-family__section-heading">
            <div className="text-xs uppercase tracking-widest text-[#0072BB] font-bold mb-3">Pricing</div>
            <h2 className="dv-size-heading text-4xl md:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              Member &amp; non-member pricing.
            </h2>
            <p className="dv-size-lede text-lg text-gray-600">
              Toggle below to compare. Pricing varies by month based on the number of weeks.
            </p>
          </div>
          <SwimPricing />
        </div>
      </section>


      {/* POOL HOURS */}
      <section id="pool-hours" className="dv-family__section dv-family__section--paper scroll-mt-20" data-testid="pool-hours-section">
        <div className="container-custom max-w-5xl">
          <div className="dv-family__section-heading">
            <div className="text-xs uppercase tracking-widest text-[#0072BB] font-bold mb-3">Schedule</div>
            <h2 className="dv-size-heading text-4xl md:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              Pool hours.
            </h2>
            <p className="dv-size-lede text-lg text-gray-600">
              Tap a day to see what&apos;s open. Brentwood location only.
            </p>
          </div>
          <PoolHoursTable />
        </div>
      </section>


      <ProgramJourney
        id="swim-inquiry" tone="aqua" testId="swim-inquiry-journey" photo="swim-hero"
        source="swim-interest" location="Brentwood" defaultInterests={['Swim Lessons']}
        eyebrow="Swim Lessons"
        title={['Ask About Swim Lessons', 'Your request, in about a minute.']}
        lede="Tell us how to reach you and our Aquatics Team will follow up."
        planTitle="Your swim request"
        reviewNoun="swim request" startLabel="Start My Swim Request" buttonText="Request Swim Information"
        successTitle="Swim Request Received"
        successMessage="Our Aquatics Team will contact you with current availability and next steps."
      />


      {/* FINAL CTA */}
      <section className="dv-family__closing">
        <div className="container-custom max-w-3xl text-center">
          <h2 className="dv-size-heading text-3xl md:text-5xl font-bold mb-4 tracking-tight leading-tight">
            The Time Is Now!
          </h2>
          <p className="dv-size-lede text-lg md:text-xl text-blue-100 mb-8">
            Join Brentwood&apos;s top fitness destination, your one-stop shop for all your
            fitness needs. Prioritize your health and join today!
          </p>
          <div className="dv-family__actions">
            <a href="#swim-inquiry">
              <Button size="lg" className="w-full sm:w-auto bg-[#0072BB] hover:bg-[#005A94] text-white font-bold h-14 px-8" data-testid="final-register-btn">
                Request Swim Information
              </Button>
            </a>
            <a href="tel:+19252402990">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/60 text-white hover:bg-white/10 hover:text-white h-14 px-8 font-semibold">
                <Phone size={18} className="mr-2" />
                Call (925) 240-2990
              </Button>
            </a>
            <Link to={publicHref("/membership")}>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto h-14 px-8 font-semibold">
                Get Started Today
              </Button>
            </Link>
          </div>
          <div className="mt-6 text-sm text-blue-200/80 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-2"><Calendar size={14} />Our Aquatics Team responds within 1 business day</span>
            <span className="hidden sm:inline">·</span>
            <a href="sms:+19253083272" className="hover:underline">Text: (925) 308-3272</a>
          </div>
        </div>
      </section>
    </div>
  );
};


export default SwimmingPage;
